import { Segmented } from 'antd';
import {
  CASHFLOW_YEAR_CALENDAR,
  CASHFLOW_YEAR_FINANCIAL,
  SUMMARY_SPEND_OVER_TIME_SECTION_ID,
  TREE_YEAR_ALL,
} from 'constants/uiConstants';
import { useCopyToClipboard } from 'hooks/useCopyToClipboard';
import dayjs from 'dayjs';
import type { GUnit } from 'interfaces/IGlobalCatalogue';
import type { IInput } from 'interfaces/IInput';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { formatCurrency } from 'utils/CurrencyUtils';
import type { RootState } from '../../../../../redux/Store';
import {
  type CashflowYearMode,
  setCashflowYearMode,
} from '../../../../../redux/slices/InsightsSlice';
import { formatCashflowYearLabel } from '../cashflow/cashflowUtils';
import type {
  PlanVsActualCategoryFilter,
  SpendMetric,
} from './computePlanVsActual';
import { computeSpendTimeline, type SpendLineItem } from './computeSpendTimeline';
import CopyButton from './CopyButton';
import { formatNumber } from './formatNumber';
import { buildTsv } from './summary2Utils';

const CHART_W = 700;
const CHART_H = 220;
// Room for a "$12,345.67/ha" tick label.
const PAD_LEFT = 72;
const PAD_RIGHT = 12;
const PAD_TOP = 12;
const PAD_BOTTOM = 28;
const PLOT_W = CHART_W - PAD_LEFT - PAD_RIGHT;
const PLOT_H = CHART_H - PAD_TOP - PAD_BOTTOM;

interface SpendTimelineChartProps {
  inputs: IInput[];
  gUnits: GUnit[];
  /** Shared with the crop-rows table/bar-chart above it in the same Plan vs Actual Spend card. */
  categoryFilter: PlanVsActualCategoryFilter;
  /** Crop this timeline is scoped to — the crop selector lives in the parent (PlanVsActualChart) so both sub-sections stay in sync. */
  cropLabel: string;
  /** Shared with the crop-rows table/bar-chart above it. */
  metric: SpendMetric;
}

interface HoverState {
  /** Window index (0-11) of the hovered month. */
  index: number;
  /** Mouse position in pixels, relative to the chart container — used to place the floating tooltip. */
  pixelX: number;
  containerWidth: number;
}

const formatItemDate = (iso: string): string => dayjs(iso).format('D MMM');

/** One series' breakdown inside the crosshair tooltip — a total plus the individual dated line items behind it. */
const SeriesBreakdown: React.FC<{
  label: string;
  pillClass: string;
  value: number;
  items: SpendLineItem[];
  scale: (v: number) => number;
  /** $ or $/ha, matching the chart's metric. */
  formatValue: (v: number) => string;
}> = ({ label, pillClass, value, items, scale, formatValue }) => (
  <div className="min-w-[11rem]">
    <div className="flex items-center gap-2">
      <span
        className={`inline-flex items-center rounded-full px-2 py-0.5 text-[0.6875rem] font-bold text-white ${pillClass}`}
      >
        {label}
      </span>
      <span className="font-semibold text-white">{formatValue(value)}</span>
    </div>
    {items.length > 0 ? (
      <ul className="mt-1 flex flex-col gap-0.5">
        {items.map((item, i) => (
          <li key={i} className="flex items-baseline justify-between gap-3">
            <span className="truncate text-gray-300">
              {item.label}{' '}
              <span className="text-gray-muted">({formatItemDate(item.date)})</span>
            </span>
            <span className="shrink-0 tabular-nums text-gray-200">
              {formatValue(scale(item.cost))}
            </span>
          </li>
        ))}
      </ul>
    ) : (
      <div className="mt-1 text-gray-muted">No costs this month</div>
    )}
  </div>
);

/**
 * The "Spend Over Time" sub-section of the Plan vs Actual Spend card —
 * content only, no SectionCard of its own (it lives inside
 * PlanVsActualChart's card). Always scoped to one crop (`inputs` is already
 * narrowed by the parent's crop selector, since a burn-down curve only
 * makes sense for a single season's plan).
 *
 * Hover is a tracked crosshair (not per-point antd Tooltips) so Plan and
 * Actual can be compared at the same month in one glance — moving the mouse
 * anywhere over the plot shows both series' value and line items at the
 * nearest month.
 *
 * Months are grouped by year within the same calendar / financial year
 * window as Cashflow (the setting is shared), and a dashed projection
 * continues from the last Actual point with the Plan still to come (OP-2389).
 */
const SpendTimelineChart: React.FC<SpendTimelineChartProps> = ({
  inputs,
  gUnits,
  categoryFilter,
  cropLabel,
  metric,
}) => {
  const dispatch = useDispatch();
  const cashflowYearMode = useSelector(
    (state: RootState) => state.insightsReducer.cashflowYearMode
  );
  // Same year as Cashflow: the tree's year filter, or the current year when
  // the tree is set to "All years".
  const treeYearFilter = useSelector(
    (state: RootState) => state.farmReducer.treeYearFilter
  );
  const effectiveYear =
    treeYearFilter === TREE_YEAR_ALL ? new Date().getFullYear() : treeYearFilter;

  const timeline = useMemo(
    () =>
      computeSpendTimeline(inputs, gUnits, categoryFilter, {
        year: effectiveYear,
        yearMode: cashflowYearMode,
      }),
    [inputs, gUnits, categoryFilter, effectiveYear, cashflowYearMode]
  );

  // Live totals' "Spend over time" link lands here via the URL hash — scroll
  // this section into view, then drop the hash so the link works again.
  const location = useLocation();
  const navigate = useNavigate();
  const sectionRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (location.hash !== `#${SUMMARY_SPEND_OVER_TIME_SECTION_ID}`) return;
    sectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    navigate(
      { pathname: location.pathname, search: location.search },
      { replace: true }
    );
  }, [location.hash, location.pathname, location.search, navigate]);

  const perHaUnavailable = metric === 'perHa' && timeline.areaHa <= 0;
  const scale = (v: number): number =>
    metric === 'perHa' && timeline.areaHa > 0 ? v / timeline.areaHa : v;
  const valueSuffix = metric === 'perHa' && !perHaUnavailable ? '/ha' : '';
  // Every on-screen figure carries its unit, so $/ha is never mistaken for $.
  const formatValue = (v: number): string =>
    `${formatCurrency(v)}${valueSuffix}`;

  const containerRef = useRef<HTMLDivElement>(null);
  const [hover, setHover] = useState<HoverState | null>(null);

  const { copied, copy } = useCopyToClipboard(() => {
    const headers = [
      'Month',
      `Plan Cumulative ($${valueSuffix})`,
      `Actual Cumulative ($${valueSuffix})`,
      `Projection Cumulative ($${valueSuffix})`,
    ];
    const body = timeline.points.map((p) => [
      p.monthLabel,
      formatNumber(scale(p.planCumulative)),
      p.index <= (timeline.lastActualIndex ?? -1)
        ? formatNumber(scale(p.actualCumulative))
        : '',
      p.projectionCumulative === null
        ? ''
        : formatNumber(scale(p.projectionCumulative)),
    ]);
    return buildTsv(headers, body);
  });

  const maxValue = Math.max(
    1,
    ...timeline.points.map((p) =>
      Math.max(
        scale(p.planCumulative),
        scale(p.actualCumulative),
        scale(p.projectionCumulative ?? 0)
      )
    )
  );

  const x = (index: number) => PAD_LEFT + (index / 11) * PLOT_W;
  const y = (value: number) => PAD_TOP + PLOT_H - (value / maxValue) * PLOT_H;

  const planPoints = timeline.points
    .map((p) => `${x(p.index)},${y(scale(p.planCumulative))}`)
    .join(' ');
  const actualPoints =
    timeline.lastActualIndex === null
      ? ''
      : timeline.points
          .slice(0, timeline.lastActualIndex + 1)
          .map((p) => `${x(p.index)},${y(scale(p.actualCumulative))}`)
          .join(' ');
  // Only drawn when it has somewhere to go past the last Actual point.
  const projectionPoints =
    timeline.lastActualIndex === null ||
    timeline.lastActualIndex >= timeline.points.length - 1
      ? ''
      : timeline.points
          .filter((p) => p.projectionCumulative !== null)
          .map((p) => `${x(p.index)},${y(scale(p.projectionCumulative ?? 0))}`)
          .join(' ');

  const gridFractions = [0, 1 / 3, 2 / 3, 1];

  const varianceToDate =
    timeline.actualToDate !== null && timeline.planToDate !== null
      ? scale(timeline.actualToDate) - scale(timeline.planToDate)
      : null;
  const varianceColorClass =
    varianceToDate === null
      ? 'text-gray-dark'
      : varianceToDate > 0
        ? 'text-danger'
        : varianceToDate < 0
          ? 'text-success'
          : 'text-gray-dark';

  const handleMouseMove = (e: React.MouseEvent<SVGRectElement>) => {
    const container = containerRef.current;
    if (!container) return;
    const rect = container.getBoundingClientRect();
    const pixelX = e.clientX - rect.left;
    const viewBoxX = (pixelX / rect.width) * CHART_W;
    const rawIndex = ((viewBoxX - PAD_LEFT) / PLOT_W) * 11;
    const index = Math.min(11, Math.max(0, Math.round(rawIndex)));
    setHover({ index, pixelX, containerWidth: rect.width });
  };
  const handleMouseLeave = () => setHover(null);

  const hoverPoint = hover ? timeline.points[hover.index] : null;
  const hoverHasActual =
    hover !== null &&
    timeline.lastActualIndex !== null &&
    hover.index <= timeline.lastActualIndex;
  const hoverProjection =
    projectionPoints && hoverPoint && hover && !hoverHasActual
      ? hoverPoint.projectionCumulative
      : null;
  const flipTooltip = hover !== null && hover.pixelX > hover.containerWidth * 0.55;

  return (
    <div
      ref={sectionRef}
      id={SUMMARY_SPEND_OVER_TIME_SECTION_ID}
      className="mt-8 scroll-mt-24 border-t border-gray-bright pt-6"
    >
      <div className="mb-3 flex flex-wrap items-center gap-3">
        <h4 className="text-base font-semibold text-charcoal">
          Spend Over Time
        </h4>
        <span className="text-sm text-gray-dark">
          {formatCashflowYearLabel(effectiveYear, cashflowYearMode)}
        </span>
        <Segmented<CashflowYearMode>
          aria-label="Year type"
          value={cashflowYearMode}
          onChange={(value) => dispatch(setCashflowYearMode(value))}
          options={[
            { label: 'Calendar', value: CASHFLOW_YEAR_CALENDAR },
            { label: 'Financial', value: CASHFLOW_YEAR_FINANCIAL },
          ]}
        />
        <CopyButton copied={copied} onCopy={copy} />
      </div>

      {timeline.outOfWindowCount > 0 && (
        <div className="mb-3 text-sm text-gray-dark">
          {timeline.outOfWindowCount}{' '}
          {timeline.outOfWindowCount === 1 ? 'cost is' : 'costs are'} dated
          outside {formatCashflowYearLabel(effectiveYear, cashflowYearMode)}{' '}
          and not shown. Change the year in the tree, or switch between
          Calendar and Financial, to see them. This setting is shared with
          Cashflow.
        </div>
      )}

      {perHaUnavailable && (
        <div className="mb-3 text-sm text-gray-muted">
          No area recorded for {cropLabel} — showing $ totals instead of $/ha.
        </div>
      )}

      {timeline.lastActualIndex === null ? (
        <span className="text-base text-gray-muted">
          No Actual spend recorded yet for {cropLabel} — nothing to compare
          against Plan yet.
        </span>
      ) : (
        <div className="mb-4 flex flex-wrap items-baseline gap-x-6 gap-y-1 text-sm">
          <span className="text-gray-muted">
            As of{' '}
            <span className="font-semibold text-charcoal">
              {timeline.lastActualDate
                ? dayjs(timeline.lastActualDate).format('D MMM')
                : timeline.points[timeline.lastActualIndex].monthLabel}
            </span>
            :
          </span>
          <span className="text-gray-dark">
            Plan to date{' '}
            <span className="font-semibold text-charcoal">
              {formatValue(scale(timeline.planToDate ?? 0))}
            </span>
          </span>
          <span className="text-gray-dark">
            Actual to date{' '}
            <span className="font-semibold text-charcoal">
              {formatValue(scale(timeline.actualToDate ?? 0))}
            </span>
          </span>
          <span className={varianceColorClass}>
            Variance{' '}
            <span className="font-semibold">
              {formatValue(varianceToDate ?? 0)}
            </span>
          </span>
          {projectionPoints && timeline.projectedTotal !== null && (
            <span className="text-gray-dark">
              Projected total{' '}
              <span className="font-semibold text-charcoal">
                {formatValue(scale(timeline.projectedTotal))}
              </span>
            </span>
          )}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
        <span className="inline-flex items-center gap-1.5 text-sm text-gray-dark">
          <span className="inline-block h-0.5 w-4 bg-chart-7" />
          Plan
        </span>
        <span className="inline-flex items-center gap-1.5 text-sm text-gray-dark">
          <span className="inline-block h-0.5 w-4 bg-chart-8" />
          Actual
        </span>
        {projectionPoints && (
          <span className="inline-flex items-center gap-1.5 text-sm text-gray-dark">
            <svg width="16" height="4" aria-hidden="true" className="shrink-0">
              <line
                x1={0}
                y1={2}
                x2={16}
                y2={2}
                className="stroke-gray-dark"
                strokeWidth={2}
                strokeDasharray="4 2"
              />
            </svg>
            Projection (Actual to date, then remaining Plan)
          </span>
        )}
        <span className="text-xs text-gray-muted">
          Move the mouse over the chart to compare Plan and Actual at any month.
        </span>
      </div>

      <div ref={containerRef} className="relative mt-2 max-w-3xl">
        <svg viewBox={`0 0 ${CHART_W} ${CHART_H}`} className="h-56 w-full">
          {gridFractions.map((f) => {
            const gy = PAD_TOP + PLOT_H - f * PLOT_H;
            return (
              <g key={f}>
                <line
                  x1={PAD_LEFT}
                  y1={gy}
                  x2={CHART_W - PAD_RIGHT}
                  y2={gy}
                  className="stroke-gray-bright"
                  strokeWidth={1}
                />
                <text
                  x={PAD_LEFT - 8}
                  y={gy + 3}
                  textAnchor="end"
                  className="fill-gray-muted text-[0.5625rem]"
                >
                  {formatValue(maxValue * f)}
                </text>
              </g>
            );
          })}

          {timeline.points.map((p) => (
            <text
              key={p.index}
              x={x(p.index)}
              y={CHART_H - PAD_BOTTOM + 16}
              textAnchor="middle"
              className="fill-gray-muted text-[0.5625rem]"
            >
              {p.monthLabel}
            </text>
          ))}

          <polyline
            points={planPoints}
            fill="none"
            className="stroke-chart-7"
            strokeWidth={2}
          />
          {actualPoints && (
            <polyline
              points={actualPoints}
              fill="none"
              className="stroke-chart-8"
              strokeWidth={2}
            />
          )}
          {projectionPoints && (
            <polyline
              points={projectionPoints}
              fill="none"
              className="stroke-gray-dark"
              strokeWidth={2}
              strokeDasharray="6 4"
            />
          )}

          {timeline.points.map((p) => (
            <circle
              key={`plan-dot-${p.index}`}
              cx={x(p.index)}
              cy={y(scale(p.planCumulative))}
              r={2.5}
              className="fill-chart-7 opacity-70"
            />
          ))}
          {timeline.lastActualIndex !== null &&
            timeline.points
              .slice(0, timeline.lastActualIndex + 1)
              .map((p) => (
                <circle
                  key={`actual-dot-${p.index}`}
                  cx={x(p.index)}
                  cy={y(scale(p.actualCumulative))}
                  r={2.5}
                  className="fill-chart-8 opacity-70"
                />
              ))}

          {hover && hoverPoint && (
            <>
              <line
                x1={x(hover.index)}
                y1={PAD_TOP}
                x2={x(hover.index)}
                y2={CHART_H - PAD_BOTTOM}
                className="stroke-charcoal"
                strokeWidth={1}
                strokeDasharray="3 3"
              />
              <circle
                cx={x(hover.index)}
                cy={y(scale(hoverPoint.planCumulative))}
                r={5}
                className="fill-chart-7 stroke-white"
                strokeWidth={1.5}
              />
              {hoverHasActual && (
                <circle
                  cx={x(hover.index)}
                  cy={y(scale(hoverPoint.actualCumulative))}
                  r={5}
                  className="fill-chart-8 stroke-white"
                  strokeWidth={1.5}
                />
              )}
            </>
          )}

          {/* Invisible interactive layer — tracks mouse position across the whole plot area to drive the crosshair. */}
          <rect
            x={PAD_LEFT}
            y={PAD_TOP}
            width={PLOT_W}
            height={PLOT_H}
            fill="transparent"
            className="cursor-crosshair"
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
          />
        </svg>

        {hover && hoverPoint && (
          <div
            className="pointer-events-none absolute top-2 z-30 flex flex-col gap-2 rounded-md bg-charcoal p-3 text-xs shadow-lg"
            style={
              flipTooltip
                ? { right: hover.containerWidth - hover.pixelX + 12 }
                : { left: hover.pixelX + 12 }
            }
          >
            <div className="flex items-center justify-between gap-3">
              <span className="font-semibold text-white">{hoverPoint.monthLabel}</span>
              <span className="text-gray-muted">
                {metric === 'total' ? 'Total' : '$/ha'}
              </span>
            </div>
            <SeriesBreakdown
              label="Plan"
              pillClass="bg-chart-7"
              value={scale(hoverPoint.planCumulative)}
              items={hoverPoint.planItems}
              scale={scale}
              formatValue={formatValue}
            />
            {hoverHasActual && (
              <SeriesBreakdown
                label="Actual"
                pillClass="bg-chart-8"
                value={scale(hoverPoint.actualCumulative)}
                items={hoverPoint.actualItems}
                scale={scale}
                formatValue={formatValue}
              />
            )}
            {hoverProjection !== null && (
              <div className="flex items-center gap-2">
                <span className="inline-flex items-center rounded-full border border-dashed border-gray-300 px-2 py-0.5 text-[0.6875rem] font-bold text-white">
                  Projection
                </span>
                <span className="font-semibold text-white">
                  {formatValue(scale(hoverProjection))}
                </span>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default SpendTimelineChart;
